<template>
	<div class="spanMappedValuesEditor">
		<div
			class="simulator__grid simulator__grid--1-2"
			v-if="checkedSpans.length"
		>
			<div></div>
			<div
				class="spanMappedValuesEditor__bulkControls grid--4 align-items-end lePadding"
			>
				<BFieldBlock :label="$t('Número de vãos')">
					<BFieldNumber
						:decimals="false"
						:step-btns="true"
						:disabled="true"
						:step="1"
						:precision="0"
						:value="checkedSpans.length"
					/>
				</BFieldBlock>

				<BFieldNumber
					v-model="bulkEditionValue"
					:placeholder="bulkEditionPlaceholder"
					v-bind="fieldProps"
				/>

				<div class="flex" style="gap: 20px">
					<BBtn
						size="m"
						color="secondary"
						shape="circle"
						@click="handleBulkEditionCancelBtnClick"
					>
						<BIcon name="Close" size="20px" />
					</BBtn>
					<BBtn
						size="m"
						color="primary"
						shape="circle"
						@click="handleBulkEditionSaveBtnClick"
					>
						<BIcon name="Save" size="20px" />
					</BBtn>
				</div>
			</div>
		</div>

		<div class="spansTable">
			<div
				class="spansTable__row spansTable__row--hasGap"
				v-if="includeConsoles && consoleSpanDescriptions.length"
			>
				<SimulatorSpanMappedValuesEditorItem
					:spanDescription="consoleSpanDescriptions[0]"
					:value="targetKey ? values.console[0][targetKey] : values.console[0]"
					:canCheck="includeConsolesInBulkEdit"
					:isChecked="isChecked(consoleSpanDescriptions[0])"
					:fieldProps="fieldProps"
					@updateValue="handleSpanValueUpdate"
					@check="handleSpanCheckChange"
				/>
			</div>

			<div class="spansTable__row spansTable__row--hasGap">
				<SimulatorSpanMappedValuesEditorItem
					v-for="(spanDescription, i) in mainSpanDescriptions"
					:key="spanDescription.id"
					:spanDescription="spanDescription"
					:canCheck="mainSpanDescriptions.length > 1"
					:value="targetKey ? values.main[i][targetKey] : values.main[i]"
					:isChecked="isChecked(spanDescription)"
					:fieldProps="fieldProps"
					@updateValue="handleSpanValueUpdate"
					@check="handleSpanCheckChange"
				/>
			</div>

			<div
				class="spansTable__row spansTable__row--hasGap"
				v-if="includeConsoles && consoleSpanDescriptions.length"
			>
				<SimulatorSpanMappedValuesEditorItem
					:spanDescription="consoleSpanDescriptions[1]"
					:canCheck="includeConsolesInBulkEdit"
					:value="targetKey ? values.console[1][targetKey] : values.console[1]"
					:isChecked="isChecked(consoleSpanDescriptions[1])"
					:fieldProps="fieldProps"
					@check="handleSpanCheckChange"
					@updateValue="handleSpanValueUpdate"
				/>
			</div>
		</div>
	</div>
</template>

<script>
/* eslint-disable no-mixed-spaces-and-tabs */
import { mapGetters } from "vuex";
import { uniq } from "@/utils";

import BBtn from "@/components/base/BBtn";
import BIcon from "@/components/base/BIcon";
import BCheckbox from "@/components/base/BCheckbox";
import BFieldBlock from "@/components/base/BFieldBlock";
import BFieldNumber from "@/components/base/BFieldNumber";

import SimulatorSpanMappedValuesEditorItem from "./SimulatorSpanMappedValuesEditorItem.vue";

export default {
	components: {
		BBtn,
		BIcon,
		BCheckbox,
		BFieldBlock,
		BFieldNumber,
		SimulatorSpanMappedValuesEditorItem,
	},

	props: {
		includeConsoles: {
			type: Boolean,
			default: true,
		},

		includeConsolesInBulkEdit: {
			type: Boolean,
			default: true,
		},

		values: {
			type: Object,
			required: true,
		},

		targetKey: {
			type: [String, Number],
			required: false,
		},

		fieldProps: {
			type: Object,
			default: () => ({
				min: 0,
			}),
		},
	},

	data() {
		return {
			checkedSpans: [],
			bulkEditionValue: null,
		};
	},

	computed: {
		...mapGetters("simulator", [
			"spanDescriptions",
			"mainSpanDescriptions",
			"consoleSpanDescriptions",
		]),

		bulkEditionPlaceholder() {
			if (this.checkedSpans.length) {
				if (!this.isSameValueAcrossCheckedSpans()) {
					return "Múltiplos valores";
				}
			}

			return "";
		},
	},

	methods: {
		getValue(itemId) {
			const { spanType, spanIndex } = this.spanDescriptions.find(
				({ id }) => id === itemId
			).details;

			return this.targetKey
				? this.values[spanType][spanIndex][this.targetKey]
				: this.values[spanType][spanIndex];
		},

		setValue(itemId, value) {
			const { spanType, spanIndex } = this.spanDescriptions.find(
				({ id }) => id === itemId
			).details;

			if (this.targetKey) {
				this.values[spanType][spanIndex][this.targetKey] = value;
			} else {
				this.values[spanType][spanIndex] = value;
			}

			this.$forceUpdate();
		},

		handleSpanCheckChange(spanDescription, isChecked) {
			if (isChecked) {
				this.checkedSpans.push(spanDescription.id);
			} else {
				this.checkedSpans = this.checkedSpans.filter(
					(id) => id !== spanDescription.id
				);
			}
		},

		handleSpanValueUpdate(spanDescription, value) {
			this.setValue(spanDescription.id, value);
		},

		isChecked(spanDescription) {
			return this.checkedSpans.includes(spanDescription.id);
		},

		isSameValueAcrossCheckedSpans() {
			return (
				uniq(
					this.checkedSpans.map((spanId) => {
						return this.getValue(spanId);
					})
				).length === 1
			);
		},

		handleBulkEditionCancelBtnClick() {
			this.checkedSpans = [];
		},

		handleBulkEditionSaveBtnClick() {
			this.checkedSpans.forEach((itemId) => {
				this.setValue(itemId, this.bulkEditionValue);
			});
		},
	},

	watch: {
		checkedSpans: {
			handler(checkedSpans) {
				if (checkedSpans.length) {
					if (this.isSameValueAcrossCheckedSpans()) {
						const sampleSpanKey = this.checkedSpans[0];
						this.bulkEditionValue = this.getValue(sampleSpanKey);
					} else {
						this.bulkEditionValue = "";
					}
				}
			},
			deep: true,
		},
	},
};
</script>

<style lang="scss">
.spanMappedValuesEditor__bulkControls {
	background: var(--color-neutral-125);
	margin-right: calc(-1 * var(--padding-inline-simulator));
	margin-bottom: 40px;
}

.spanMappedValuesEditor {
	.spansTable__row {
		grid-template-columns: repeat(auto-fill, minmax(min(284px, 100%), 1fr));
	}
}
</style>
